import * as Yup from 'yup';

import { createEmailValidation } from '@/utils/emailValidation';
import { createZipcodeValidation } from '@/utils/zipcodeValidation';

const createPhoneValidation = () => {
  return Yup.string()
    .required('Este campo es requerido')
    .test('phone-format', function (value) {
      if (!value) return false;

      const phoneConfig: Record<string, { prefix: string; digits: number }> = {
        '+52': { prefix: '+52', digits: 10 },
        '+51': { prefix: '+51', digits: 9 },
        '+57': { prefix: '+57', digits: 10 },
        '+56': { prefix: '+56', digits: 9 },
      };

      const matchedConfig = Object.values(phoneConfig).find((cfg) => value.startsWith(cfg.prefix));

      if (!matchedConfig) {
        return this.createError({
          message: 'El número debe incluir un código de país válido (+52, +56, +57, +51)',
        });
      }

      const phoneNumber = value.substring(matchedConfig.prefix.length);

      if (!/^\d+$/.test(phoneNumber)) {
        return this.createError({
          message: `El número debe tener solo dígitos después de ${matchedConfig.prefix}`,
        });
      }

      if (phoneNumber.length !== matchedConfig.digits) {
        return this.createError({
          message: `El número debe tener ${matchedConfig.prefix} + ${matchedConfig.digits} dígitos`,
        });
      }

      return true;
    });
};

const personSchema = Yup.object().shape({
  firstName: Yup.string().required('Este campo es requerido'),
  lastName: Yup.string().required('Este campo es requerido'),
  phone: createPhoneValidation(),
  email: createEmailValidation('Debe tener un formato válido con dominio'),
});

const originAddressSchema = Yup.object().shape({
  showForm: Yup.boolean(),
  lat: Yup.string(),
  lng: Yup.string(),
  address: Yup.string().required('Este campo es requerido'),
  country: Yup.string(),
  reference: Yup.string(),
  zipCode: createZipcodeValidation('country'),
  city: Yup.string().required('Este campo es requerido'),
  externalNumber: Yup.string()
    .when('showForm', (showForm, schema) => {
      return showForm[0]
        ? schema.required('Este campo es requerido')
        : schema.optional().nullable();
    })
    .typeError('Deben ser solo números '),
  internalNumber: Yup.string(),
  cologne: Yup.string().when('showForm', (showForm, schema) => {
    return showForm[0] ? schema.required('Este campo es requerido') : schema.optional().nullable();
  }),
  state: Yup.string().when('showForm', (showForm, schema) => {
    return showForm[0] ? schema.required('Este campo es requerido') : schema.optional().nullable();
  }),
});

const destinationAddressSchema = Yup.object().shape({
  showForm: Yup.boolean(),
  lat: Yup.string(),
  lng: Yup.string(),
  country: Yup.string(),
  reference: Yup.string(),
  zipCode: createZipcodeValidation('country'),
  city: Yup.string().required('Este campo es requerido'),
  address: Yup.string().required('Este campo es requerido'),
  externalNumber: Yup.string()
    .when('showForm', (showForm, schema) => {
      return showForm[0]
        ? schema.required('Este campo es requerido')
        : schema.optional().nullable();
    })
    .typeError('Deben ser solo números '),
  internalNumber: Yup.string(),
  cologne: Yup.string().when('showForm', (showForm, schema) => {
    return showForm[0] ? schema.required('Este campo es requerido') : schema.optional().nullable();
  }),
  state: Yup.string().when('showForm', (showForm, schema) => {
    return showForm[0] ? schema.required('Este campo es requerido') : schema.optional().nullable();
  }),
});

const codSchema = Yup.object().shape({
  cod: Yup.boolean(),
  amount: Yup.number()
    .typeError('Debe ingresar solo números')
    .when('$codMin', (codMin, schema) =>
      schema.min(codMin[0] as unknown as number, `El mínimo es ${codMin[0]}`),
    )
    .when('$codMax', (codMax, schema) =>
      schema.max(codMax[0] as unknown as number, `El máximo es ${codMax[0]}`),
    )
    .when('cod', (cod, schema) => {
      return cod[0] ? schema.required('Este campo es requerido') : schema;
    }),
  currency: Yup.string(),
});

const paymentsSchema = Yup.object().shape({
  insured: Yup.boolean(),
  insuredValue: Yup.number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === undefined || originalValue === null
        ? undefined
        : value,
    )
    .nullable()
    .optional()
    .typeError('Debe ingresar solo números')
    .when('$insMin', (insMin, schema) =>
      insMin && insMin[0] !== undefined
        ? schema.min(insMin[0] as unknown as number, `El mínimo es ${insMin[0]}`)
        : schema,
    )
    .when('$insMaX', (insMaX, schema) =>
      insMaX && insMaX[0] !== undefined
        ? schema.max(insMaX[0] as unknown as number, `El máximo es ${insMaX[0]}`)
        : schema,
    )
    .when('insured', (insured, schema) => {
      return insured[0] ? schema.required('Este campo es requerido') : schema.optional().nullable();
    }),
  cashOnDelivery: codSchema,
});

const optionsSchema = Yup.object().shape({
  scheduleNow: Yup.boolean(),
  pickUpAfter: Yup.string().when('scheduleNow', (scheduleNow, schema) => {
    return scheduleNow[0] ? schema.required('Este campo es requerido') : schema;
  }),
  notes: Yup.string(),
});

const itemsSchema = Yup.object().shape({
  size: Yup.string().required('Este campo es requerido'),
  description: Yup.string(),
  weight: Yup.number().default(0).required('Este campo es requerido'),
  length: Yup.string().default('0').typeError('Debe ingresar solo números'),
  width: Yup.string().default('0').typeError('Debe ingresar solo números'),
  height: Yup.string().default('0').typeError('Debe ingresar solo números'),
  peso: Yup.number().default(0),
});

export const schema = Yup.object().shape({
  sender: personSchema,
  recipient: personSchema,
  origin: originAddressSchema,
  destination: destinationAddressSchema,
  payments: paymentsSchema,
  options: optionsSchema,
  items: itemsSchema,
  internalKey: Yup.string()
    .test(
      'internal-key-format',
      'Solo se permiten letras, números, guiones y guiones bajos',
      function (value) {
        if (!value || value.trim().length === 0) return true;
        const regex = /^[a-zA-Z0-9-_]+$/;
        return regex.test(value.trim());
      },
    )
    .test('internal-key-length', 'Debe tener entre 5 y 100 caracteres', function (value) {
      if (!value || value.trim().length === 0) return true;
      const trimmed = value.trim();
      return trimmed.length >= 5 && trimmed.length <= 100;
    }),
  deliveryType: Yup.mixed()
    .oneOf(['SMD', 'SPT', 'CO2F', '99M', 'RET', 'NAL', 'ECO', 'TLM'], 'Valor no válido')
    .required('Este campo es requerido'),
  draft: Yup.boolean(),
  saveOriginAddress: Yup.boolean().optional(),
  saveDestinationAddress: Yup.boolean().optional(),
});

export type CreateOrderFormType = Yup.InferType<typeof schema>;
export type CreateOrderFormTypeKeys = keyof CreateOrderFormType;
